import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { Email, ViewStream } from '@mui/icons-material';
import PDFMerger from 'pdf-merger-js';

import { PDFReader } from '@/shared/components/ui/PDF';
import { useUiConfirmModalStore } from '@/store/ui';
import { Contrato } from '@/shared/interfaces';
import {
  useFetchGetHistorialConsolidado,
  useFetchPostHistorialConsolidado,
} from '@/shared/actions/contrato/historial_consolidado.actions';
import { useDocumentacionContratoStore } from '../../hooks/documentacion-contrato.store';
import { Documentacion } from '@/shared/interfaces/erp/documentos/Documentacion';
import { HistorialConsolidado } from '@/shared/interfaces/erp/contrato/historial_consolidado.interface';
import { formatDateWithTime } from '@/shared';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';


const defaultDocumentacion = {
  estado: true,
  fecha_creacion: new Date().toISOString(),
  fecha_modificacion: new Date().toISOString(),
  tipo: 'pdf',
  usuario_username: '',
  modelo: 'CONTRATO',
  referencia_modelo: 'CONTRATO',
  size: '10',
  usuario: '—'
}

const NOMBRE_ARCHIVO_CONSOLIDADO = 'DOCUMENTO_CONSOLIDADO.pdf';

type TabEnvioDocumentosAuditoriaInstallProps = {
  contrato: Contrato;
};

export const TabEnvioDocumentosAuditoriaInstall =
  ({ contrato }: TabEnvioDocumentosAuditoriaInstallProps) => {
    const [seleccionados, setSeleccionados] = useState<string[]>([]);
    const [para, setPara] = useState(contrato.cliente_data?.email || '');
    const [asunto, setAsunto] = useState('CONTRATO DE SERVICIO DE INTERNET');
    const [mensaje, setMensaje] = useState(`Estimado(a) ${contrato.cliente_data?.razon_social || '[NOMBRE DEL CLIENTE]'}
En respuesta a su solicitud, adjunto encontrará el Contrato de Servicio de Internet
Ha sido un gusto atenderle`);
    const [pdfConsolidadoUrl, setPdfConsolidadoUrl] = useState<string | null>(null);
    const [pdfConsolidadoBlob, setPdfConsolidadoBlob] = useState<Blob | null>(null);
    const [generandoPdf, setGenerandoPdf] = useState(false);
    const [errorPdf, setErrorPdf] = useState<string | null>(null);
    const [mostrarHistorial, setMostrarHistorial] = useState(false);

    const { mutate: postHistorialConsolidado, isPending: enviando } =
      useFetchPostHistorialConsolidado();

    const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
    const setConfirmDialogIsOpen = useUiConfirmModalStore(
      (s) => s.setConfirmDialogIsOpen
    );

    const documentosStore = useDocumentacionContratoStore((s) => s.documentos);

    const documentosIniciales = useMemo<Documentacion[]>(() => {
      const defaults: Documentacion[] = [
        {
          codigo: 'contrato',
          id_documentacion: 'contrato',
          url: contrato.url_contrato || null,
          nombre: 'contrato.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_1',
          id_documentacion: 'url_anexo_1',
          url: contrato.url_anexo_1 || null,
          nombre: 'anexo_1.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_1_f',
          id_documentacion: 'url_anexo_1_f',
          url: contrato.url_anexo_1_f || null,
          nombre: 'anexo_1_f.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_1_orden',
          id_documentacion: 'url_anexo_1_orden',
          url: contrato.url_anexo_1_orden || null,
          nombre: 'anexo_1_orden.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_2',
          id_documentacion: 'url_anexo_2',
          url: contrato.url_anexo_2 || null,
          nombre: 'anexo_2.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_2_f',
          id_documentacion: 'url_anexo_2_f',
          url: contrato.url_anexo_2_f || null,
          nombre: 'anexo_2_f.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_3_f',
          id_documentacion: 'url_anexo_3_f',
          url: contrato.url_anexo_3_f || null,
          nombre: 'anexo_3_f.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_2_orden',
          id_documentacion: 'url_anexo_2_orden',
          url: contrato.url_anexo_2_orden || null,
          nombre: 'anexo_2_orden.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_anexo_1_tec',
          id_documentacion: 'url_anexo_1_tec',
          url: contrato.url_anexo_1_tec || null,
          nombre: 'anexo_1_tec.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_solicitud_beneficio',
          id_documentacion: 'url_solicitud_beneficio',
          url: contrato.url_solicitud_beneficio || null,
          nombre: 'solicitud_beneficio.pdf',
          ...defaultDocumentacion
        },
        {
          codigo: 'url_autorizacion_buro',
          id_documentacion: 'url_autorizacion_buro',
          url: contrato.url_autorizacion_buro || null,
          nombre: 'autorizacion_buro.pdf',
          ...defaultDocumentacion
        },
      ].filter(e => e.url != null);

      return [...defaults, ...documentosStore];
    }, [
      contrato.url_contrato,
      contrato.url_anexo_1_f,
      contrato.url_anexo_1_orden,
      contrato.url_autorizacion_buro,
      contrato.url_anexo_2_f,
      contrato.url_anexo_3_f,
      contrato.url_anexo_2_orden,
      contrato.url_anexo_1_tec,
      contrato.url_solicitud_beneficio,
      documentosStore,
    ]);

    const { data: historialData, isLoading: cargandoHistorial } =
      useFetchGetHistorialConsolidado(contrato.codigo ?? '', mostrarHistorial);

    const handleOpenHistorial = () => setMostrarHistorial(true);
    const handleCloseHistorial = () => setMostrarHistorial(false);

    const cortarTextoPorComas = (texto: string): string => {
      if (!texto) return texto;
      return texto.split(',').join('\n');
    };

    const CeldaTextoLargo = ({ texto }: { texto: string }) => (
      <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {cortarTextoPorComas(texto)}
      </Box>
    );

    const historialColumns = useMemo<MRT_ColumnDef<HistorialConsolidado>[]>(
      () => [
        {
          accessorKey: 'correo',
          header: 'Correo',
          Cell: ({ row }) => <CeldaTextoLargo texto={row.original.correo} />,
        },
        {
          accessorKey: 'documentos',
          header: 'Documentos',
          Cell: ({ row }) => <CeldaTextoLargo texto={row.original.documentos} />,
        },
        {
          accessorKey: 'estado_historial_consolidado',
          header: 'Estado',
          size: 150,
          Cell: ({ row }) => (
            <Chip
              variant="filled"
              color="success"
              label={cortarTextoPorComas(row.original.estado_historial_consolidado)}
            />
          ),
        },
        {
          accessorKey: 'usuario',
          header: 'Usuario',
          Cell: ({ row }) => <CeldaTextoLargo texto={row.original.usuario} />,
        },
        {
          accessorKey: 'fecha_creacion',
          header: 'Fecha Creación',
          Cell: ({ row }) => <CeldaTextoLargo texto={formatDateWithTime(row.original.fecha_creacion)} />,
        },
      ],
      []
    );

    const historialTable = useMaterialReactTable({
      columns: historialColumns,
      data: historialData?.data?.data || [],
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: true,
      enableSorting: false,
      enableBottomToolbar: true,
      enableTopToolbar: false,
      muiTableContainerProps: {
        sx: {
          maxHeight: '400px',
        },
      },
    });

    const todosSeleccionados =
      seleccionados.length === documentosIniciales.length &&
      documentosIniciales.length > 0;
    const algunosSeleccionados =
      seleccionados.length > 0 &&
      seleccionados.length < documentosIniciales.length;

    const handleToggle = (id: string) => {
      setSeleccionados((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      );
    };

    const handleToggleTodos = () => {
      if (todosSeleccionados) {
        setSeleccionados([]);
      } else {
        setSeleccionados(documentosIniciales.map((doc) => doc.id_documentacion));
      }
    };

    const documentosSeleccionados = useMemo(
      () => documentosIniciales.filter((doc) => seleccionados.includes(doc.id_documentacion)),
      [seleccionados]
    );

    const limpiarPdfConsolidado = () => {
      if (pdfConsolidadoUrl) {
        URL.revokeObjectURL(pdfConsolidadoUrl);
      }
      setPdfConsolidadoUrl(null);
      setPdfConsolidadoBlob(null);
      setErrorPdf(null);
    };

    useEffect(() => {
      return () => {
        if (pdfConsolidadoUrl) {
          URL.revokeObjectURL(pdfConsolidadoUrl);
        }
      };
    }, [pdfConsolidadoUrl]);

    const handleGenerarPdf = async () => {
      if (documentosSeleccionados.length === 0) return;

      limpiarPdfConsolidado();
      setGenerandoPdf(true);

      try {
        const merger = new PDFMerger();
        for (const doc of documentosSeleccionados) {
          await merger.add(doc.url);
        }
        const blob = await (
          merger as unknown as { saveAsBlob(): Promise<Blob> }
        ).saveAsBlob();
        const url = URL.createObjectURL(blob);
        setPdfConsolidadoUrl(url);
        setPdfConsolidadoBlob(blob);
      } catch (error) {
        console.error('Error al generar PDF consolidado:', error);
        setErrorPdf(
          'No se pudo generar el PDF consolidado. Verifique que los documentos sean accesibles.'
        );
      } finally {
        setGenerandoPdf(false);
      }
    };

    const handleDescargar = () => {
      if (!pdfConsolidadoUrl) return;
      const link = document.createElement('a');
      link.href = pdfConsolidadoUrl;
      link.download = `${contrato.cliente_data?.razon_social?.replace(/[^a-zA-Z0-9]/g, '_')}_${NOMBRE_ARCHIVO_CONSOLIDADO}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const handleBorrar = () => {
      setConfirmDialog({
        isOpen: true,
        title: 'Borrar PDF consolidado',
        subtitle: '¿Está seguro de que desea eliminar el PDF consolidado generado?',
        onConfirm: () => {
          limpiarPdfConsolidado();
          setConfirmDialogIsOpen(false);
        },
        onClose: () => setConfirmDialogIsOpen(false),
        cancelTextBtn: 'Cancelar',
        confirmTextBtn: 'Borrar',
      });
    };

    const handleEnviar = () => {
      if (!pdfConsolidadoBlob || !pdfConsolidadoUrl) return;

      const file = new File(
        [pdfConsolidadoBlob],
        NOMBRE_ARCHIVO_CONSOLIDADO,
        { type: 'application/pdf' }
      );

      postHistorialConsolidado({
        file,
        para,
        asunto,
        mensaje,
        documentos: documentosSeleccionados.map((doc) => doc.nombre).join(','),
        contrato: contrato.codigo ?? '',
      });
    };

    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Columna 1: Documentos disponibles */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <ViewStream color="primary" />
                Documentos disponibles
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={todosSeleccionados}
                    indeterminate={algunosSeleccionados}
                    onChange={handleToggleTodos}
                  />
                }
                label="Seleccionar todos"
              />
              <List
                dense
                sx={{
                  maxHeight: '400px',
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {documentosIniciales.map((doc) => {
                  const seleccionado = seleccionados.includes(doc.id_documentacion);
                  return (
                    <ListItem
                      key={doc.id_documentacion}
                      disablePadding
                      sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <ListItemButton
                        dense
                        onClick={() => handleToggle(doc.id_documentacion)}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 1,
                          }}
                        >
                          <Checkbox
                            edge="start"
                            checked={seleccionado}
                            tabIndex={-1}
                            disableRipple
                            sx={{ p: 0 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.nombre}
                          //secondary={`${doc.size}`}
                          sx={{ m: 0 }}
                        />
                      </ListItemButton>
                      <Box
                        component="iframe"
                        src={doc.url!}
                        title={doc.nombre}
                        sx={{
                          width: '100%',
                          maxHeight: 180,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Grid>

          {/* Columna 2: Vista previa y consolidado */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={
                  generandoPdf ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ViewStream />
                  )
                }
                disabled={
                  documentosSeleccionados.length === 0 || generandoPdf
                }
                onClick={handleGenerarPdf}
              >
                {generandoPdf ? 'GENERANDO PDF...' : 'GENERAR PDF CONSOLIDADO'}
              </Button>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="h6" gutterBottom>
                Vista previa
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDescargar}
                  disabled={!pdfConsolidadoUrl || generandoPdf}
                  fullWidth
                >
                  Descargar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleBorrar}
                  disabled={!pdfConsolidadoUrl || generandoPdf}
                  fullWidth
                >
                  Borrar
                </Button>
              </Stack>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {documentosSeleccionados.length} documento(s) seleccionado(s)
              </Typography>

              {errorPdf && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {errorPdf}
                </Typography>
              )}

              {pdfConsolidadoUrl ? (
                <PDFReader fileUrl={pdfConsolidadoUrl} />
              ) : (
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 4,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <PictureAsPdfIcon
                    sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                  />
                  <Typography variant="body2">
                    Selecciona documentos y genera el PDF consolidado para
                    previsualizarlo.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Columna 3: Formulario de envío */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="h6"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Email color="primary" />
                  Enviar documentos
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenHistorial}
                >
                  Historial
                </Button>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Para"
                  placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
                  helperText="Nota: Para enviar a más correos se debe separar por una coma"
                  value={para}
                  onChange={(e) => setPara(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Asunto"
                  placeholder="Asunto del correo"
                  value={asunto}
                  onChange={(e) => setAsunto(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Mensaje"
                  placeholder="Escribe el mensaje aquí..."
                  multiline
                  rows={6}
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  fullWidth
                />
                {pdfConsolidadoUrl && (
                  <Alert severity="success">
                    Archivo consolidado adjunto y listo para enviar
                  </Alert>
                )}
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    enviando ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  fullWidth
                  size="large"
                  disabled={!pdfConsolidadoUrl || generandoPdf || enviando}
                  onClick={handleEnviar}
                >
                  {enviando ? 'ENVIANDO...' : 'Enviar'}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Dialog
          open={mostrarHistorial}
          onClose={handleCloseHistorial}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>☰ Historial de envíos consolidados</DialogTitle>
          <DialogContent>
            {cargandoHistorial ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 4,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <MaterialReactTable table={historialTable} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseHistorial} color="primary">
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };
